<script>
import Layout from '@layouts/main'
import PageHeader from '@components/page-header'

import axios from 'axios'
import { evaluateIndicator, isPostureFinding, postureExclusionNote, responseMs } from '@utils/sla-contract'
import percentile from '@utils/percentile'
import formatDuration from '@utils/format-duration'
import { SEVERITIES, bogotaDay } from '@utils/severities'

const KPIS = [
  { indicator: 'mttd', group: 'critical', label: 'MTTD Critical', scope: 'critical + high', icon: 'eye' },
  { indicator: 'mttd', group: 'nonCritical', label: 'MTTD Non-critical', scope: 'medium + low', icon: 'search' },
  { indicator: 'mttr', group: 'critical', label: 'MTTR Critical', scope: 'critical + high', icon: 'zap' },
  { indicator: 'mttr', group: 'nonCritical', label: 'MTTR Non-critical', scope: 'medium + low', icon: 'shield' }
]

const DAY = 24 * 60 * 60 * 1000

export default {
  page: {
    title: 'Service SLA',
    meta: [{ name: 'description', content: 'description' }]
  },
  components: { Layout, PageHeader },
  data () {
    return {
      title: 'Service SLA',
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      items: [
        {
          text: 'Home',
          href: '/'
        },
        {
          text: 'Service SLA',
          active: true
        }
      ],
      severities: SEVERITIES,
      data: [],
      excludedCount: 0,
      loadedAt: Date.now(),
      loading: true,
      loadError: false
    }
  },
  computed: {
    kpis () {
      const results = {
        mttd: evaluateIndicator('mttd', this.data, this.loadedAt),
        mttr: evaluateIndicator('mttr', this.data, this.loadedAt)
      }
      return KPIS.map(kpi => {
        const pct = results[kpi.indicator][kpi.group].pct
        return { ...kpi, pct, value: pct === null ? 'No data' : pct.toFixed(2) + ' %' }
      })
    },
    severityStats () {
      const total = this.data.length
      return SEVERITIES.map(severity => {
        const incidents = this.data.filter(inc => inc.severity === severity.key)
        // Minutes to first response, same event as the MTTR indicator
        const minutes = incidents
          .filter(inc => responseMs(inc))
          .map(inc => (responseMs(inc) - inc.creation_ms) / 60000)
        return {
          ...severity,
          count: incidents.length,
          share: total ? (incidents.length / total) * 100 : 0,
          medianResponse: percentile(minutes, 0.5)
        }
      })
    },
    days () {
      // The data window is the last 30 x 24h, which touches 31 calendar days
      const days = []
      for (let ms = this.loadedAt - 30 * DAY; ms <= this.loadedAt; ms += DAY) days.push(bogotaDay(ms))
      return days
    },
    dailySeries () {
      const counts = {}
      this.data.forEach(inc => {
        const key = `${bogotaDay(inc.creation_ms)}|${inc.severity}`
        counts[key] = (counts[key] || 0) + 1
      })
      return SEVERITIES.map(severity => ({
        name: severity.label,
        data: this.days.map(day => counts[`${day}|${severity.key}`] || 0)
      }))
    },
    dailyOptions () {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit' },
        colors: SEVERITIES.map(severity => severity.color),
        plotOptions: {
          bar: { columnWidth: '60%', borderRadius: 3, borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' }
        },
        // 1px white separation between stacked segments
        stroke: { show: true, width: 1, colors: ['#ffffff'] },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left', markers: { radius: 2 } },
        grid: { borderColor: '#eef0f3', xaxis: { lines: { show: false } } },
        xaxis: {
          categories: this.days.map(day => day.slice(5)),
          tickAmount: 10,
          labels: { rotate: 0, style: { colors: '#98a6ad' } },
          axisTicks: { show: false }
        },
        yaxis: { labels: { formatter: value => Math.round(value), style: { colors: '#98a6ad' } } },
        tooltip: { shared: true, intersect: false, y: { formatter: value => `${value} incidents` } }
      }
    },
    exclusionNote () {
      return postureExclusionNote(this.excludedCount)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    formatDuration,
    loadData () {
      let that = this
      let baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      that.loading = true
      that.loadError = false
      axios.get(`${baseURL}/pan/xdr/report/service?include=incident.incident,by360&created_after_minutes=43200&limit=9999&include_unresponded=yes`).then((response) => {
        if (response.status === 200) {
          const toMs = value => (value && value['$date'] ? new Date(value['$date']).getTime() : null)
          that.loadedAt = Date.now()
          const incidents = response.data.data.map(function (entry) {
            return {
              incident_id: entry.incident_id,
              status: entry.status,
              severity: entry.severity,
              creation_ms: toMs(entry.creation_time),
              modification_ms: toMs(entry.modification_time),
              mttr_ms: toMs(entry.mttr_time),
              resolved_ms: entry.resolved_timestamp || null,
              incident_sources: entry.incident_sources || []
            }
          })
          // Cloud posture findings are outside the MDR service scope
          that.data = incidents.filter(inc => !isPostureFinding(inc.incident_sources))
          that.excludedCount = incidents.length - that.data.length
        }
      })
        .catch(function (error) {
          that.loadError = true
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('idToken')
            that.$router.push({ name: 'Home' })
          }
        })
        .finally(function () {
          that.loading = false
        })
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader
      :title="title"
      :items="items"
    />
    <b-alert
      :show="loading"
      variant="info"
      class="d-flex align-items-center"
    >
      <b-spinner
        small
        class="mr-2"
      />
      Loading incidents from the last 30 days, this can take a few seconds...
    </b-alert>
    <b-alert
      :show="loadError && !loading"
      variant="danger"
      class="d-flex align-items-center justify-content-between"
    >
      Could not load the SLA data. Please try again.
      <b-button
        size="sm"
        variant="outline-danger"
        @click="loadData"
      >
        Retry
      </b-button>
    </b-alert>

    <div class="sla-page">
      <p
        v-if="!loading && !loadError"
        class="text-muted mb-3"
      >
        Last 30 days · {{ data.length.toLocaleString() }} incidents
        <span
          v-if="excludedCount"
          v-b-tooltip.hover
          :title="exclusionNote"
          class="help-tip ml-1"
        >
          <feather
            type="info"
            class="icon-xs"
          />
        </span>
      </p>

      <!-- KPI tiles -->
      <div class="row">
        <div
          v-for="kpi in kpis"
          :key="kpi.indicator + kpi.group"
          class="col-xl-3 col-md-6 mb-4"
        >
          <div class="card h-100 mb-0">
            <div class="card-body">
              <div class="d-flex align-items-start">
                <div class="flex-grow-1">
                  <h5 class="text-muted font-size-13 mb-1">
                    {{ kpi.label }}
                  </h5>
                  <span class="text-muted font-size-11">{{ kpi.scope }}</span>
                </div>
                <div class="avatar-sm rounded-circle bg-soft-primary">
                  <span class="avatar-title bg-transparent text-primary">
                    <feather
                      :type="kpi.icon"
                      class="icon-sm"
                    />
                  </span>
                </div>
              </div>
              <b-skeleton
                v-if="loading"
                width="50%"
                height="2rem"
                class="mt-3"
              />
              <h3
                v-else
                class="mt-3 mb-2"
              >
                {{ loadError ? '—' : kpi.value }}
              </h3>
              <div class="sla-meter">
                <div
                  class="sla-meter-fill"
                  :style="{ width: (loading || loadError ? 0 : kpi.pct || 0) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily incidents + distribution -->
      <div class="row">
        <div class="col-xl-8 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="font-size-14 mb-1">
                <feather
                  type="bar-chart-2"
                  class="icon-xs mr-1 text-muted"
                />
                Incidents per day
              </h5>
              <b-skeleton-img
                v-if="loading"
                no-aspect
                height="280px"
              />
              <apexchart
                v-else-if="!loadError"
                type="bar"
                height="280"
                :series="dailySeries"
                :options="dailyOptions"
              />
            </div>
          </div>
        </div>
        <div class="col-xl-4 mb-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="font-size-14 mb-3">
                <feather
                  type="pie-chart"
                  class="icon-xs mr-1 text-muted"
                />
                Distribution
              </h5>
              <div
                v-for="severity in severityStats"
                :key="severity.key"
                class="d-flex align-items-center py-2 border-bottom"
              >
                <span
                  class="sla-swatch mr-2"
                  :style="{ background: severity.color }"
                />
                <span class="flex-grow-1">{{ severity.label }}</span>
                <b-skeleton
                  v-if="loading"
                  width="60px"
                />
                <template v-else-if="!loadError">
                  <strong class="mr-3">{{ severity.count.toLocaleString() }}</strong>
                  <span class="text-muted sla-share">{{ Math.round(severity.share) }}%</span>
                </template>
              </div>
              <div
                v-if="!loading && !loadError"
                class="sla-stack mt-3"
              >
                <div
                  v-for="severity in severityStats"
                  :key="severity.key"
                  v-b-tooltip.hover
                  :title="`${severity.label}: ${severity.count.toLocaleString()} incidents`"
                  :style="{ width: severity.share + '%', background: severity.color }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Typical response time -->
      <div class="card">
        <div class="card-body">
          <h5 class="font-size-14 mb-3">
            <feather
              type="clock"
              class="icon-xs mr-1 text-muted"
            />
            Typical response time (median)
            <span
              v-b-tooltip.hover
              title="Half of the incidents of each severity got their first response within this time. The median is used so a few very long cases don't distort the figure."
              class="help-tip ml-1"
            >
              <feather
                type="help-circle"
                class="icon-xs"
              />
            </span>
          </h5>
          <div class="row">
            <div
              v-for="severity in severityStats"
              :key="severity.key"
              class="col-md-3 col-6 mb-2"
            >
              <span class="text-muted">
                <span
                  class="sla-swatch mr-1"
                  :style="{ background: severity.color }"
                />
                {{ severity.label }}
              </span>
              <b-skeleton
                v-if="loading"
                width="50%"
                height="1.6rem"
                class="mt-1"
              />
              <h3
                v-else
                class="mt-1 mb-0"
              >
                {{ loadError ? '—' : formatDuration(severity.medianResponse) }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* Thin outline keeps the light medium-yellow swatch visible on white */
.sla-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
}
.sla-share {
  width: 38px;
  text-align: right;
}
/* Same-hue track: the meter shows the share, it does not judge it */
.sla-meter {
  height: 6px;
  overflow: hidden;
  background: #e6e9fe;
  border-radius: 3px;
}
.sla-meter-fill {
  height: 100%;
  background: #3449d6;
  border-radius: 3px;
  transition: width 0.4s ease;
}
/* Part-to-whole bar; 2px gaps keep adjacent severities apart */
.sla-stack {
  display: flex;
  gap: 2px;
  height: 10px;
  overflow: hidden;
  border-radius: 3px;
}
.help-tip {
  color: #ced4da;
  cursor: help;
}
.help-tip:hover {
  color: #98a6ad;
}
</style>
